import React, { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoFixNormalOutlinedIcon from "@mui/icons-material/AutoFixNormalOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RedoIcon from "@mui/icons-material/Redo";
import RemoveIcon from "@mui/icons-material/Remove";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TitleIcon from "@mui/icons-material/Title";
import UndoIcon from "@mui/icons-material/Undo";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import WidthFullIcon from "@mui/icons-material/SettingsEthernet";

const SHAPES = [
  {
    id: "rect",
    label: "Rectangle",
    key: "R",
    icon: <CropSquareIcon fontSize="small" />,
  },
  {
    id: "oval",
    label: "Oval",
    key: "O",
    icon: <RadioButtonUncheckedIcon fontSize="small" />,
  },
  {
    id: "arrow",
    label: "Arrow",
    key: "A",
    icon: <CallMadeIcon fontSize="small" />,
  },
  {
    id: "line",
    label: "Line",
    key: "L",
    icon: <HorizontalRuleIcon fontSize="small" />,
  },
];

const STAMPS = [
  {
    id: "tick",
    label: "Tick",
    key: "C",
    icon: <CheckIcon fontSize="small" color="success" />,
  },
  {
    id: "cross",
    label: "Cross",
    key: "X",
    icon: <CloseIcon fontSize="small" color="error" />,
  },
  {
    id: "mark",
    label: "Circle mark",
    key: "M",
    icon: <LooksOneOutlinedIcon fontSize="small" />,
  },
  {
    id: "qmark",
    label: "Question tag",
    key: "Q",
    icon: <SellOutlinedIcon fontSize="small" />,
  },
];

function ToolButton({ label, hint, icon, active, disabled, onClick, menu }) {
  const button = (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      sx={{
        minWidth: 58,
        height: 54,
        px: 0.75,
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.25,
        border: "1px solid",
        borderColor: active ? "#b9d2fa" : "transparent",
        borderRadius: 1.5,
        bgcolor: active ? "#e8f1ff" : "transparent",
        color: active ? "primary.main" : "#34415a",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.38 : 1,
        font: "inherit",
        "&:hover:not(:disabled)": { bgcolor: active ? "#e8f1ff" : "#f2f6fb" },
      }}
    >
      <Box display="flex" alignItems="center">
        {icon}
        {menu && <ArrowDropDownIcon sx={{ fontSize: 16, mr: -0.5 }} />}
      </Box>
      <Typography component="span" sx={{ fontSize: 11, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Tooltip arrow title={hint || label}>
      <span>{button}</span>
    </Tooltip>
  );
}

function ToolMenu({ label, items, tool, fallback, disabled, onTool }) {
  const [anchor, setAnchor] = useState(null);
  const current = items.find((item) => item.id === tool) || fallback;

  return (
    <>
      <ToolButton
        label={label}
        icon={current.icon}
        active={items.some((item) => item.id === tool)}
        disabled={disabled}
        menu
        onClick={(event) => setAnchor(event.currentTarget)}
      />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            selected={item.id === tool}
            onClick={() => {
              onTool(item.id);
              setAnchor(null);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
              {item.key}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const Separator = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
);

export default function AnnotationToolbar({
  tool,
  onTool,
  view,
  onZoom,
  onFit,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  readOnly,
  showPanel,
  onTogglePanel,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        minHeight: 64,
        px: 1,
        overflowX: "auto",
        bgcolor: "#fff",
        borderBottom: "1px solid #dfe7f3",
      }}
    >
      <ToolButton
        label="Select"
        hint="Select and move (V)"
        icon={<NearMeOutlinedIcon fontSize="small" />}
        active={tool === "select"}
        disabled={readOnly}
        onClick={() => onTool("select")}
      />
      <ToolButton
        label="Pan"
        hint="Pan (H), or hold Space"
        icon={<PanToolOutlinedIcon fontSize="small" />}
        active={tool === "pan"}
        onClick={() => onTool("pan")}
      />
      <Separator />

      <Box
        display="flex"
        alignItems="center"
        sx={{ border: "1px solid #dfe7f3", borderRadius: 1.5, mx: 0.5 }}
      >
        <Tooltip arrow title="Zoom out (Ctrl -)">
          <IconButton
            size="small"
            aria-label="Zoom out"
            onClick={() => onZoom(-0.1)}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="body2"
          sx={{ width: 50, textAlign: "center", fontWeight: 600 }}
        >
          {Math.round(view.scale * 100)}%
        </Typography>
        <Tooltip arrow title="Zoom in (Ctrl +)">
          <IconButton
            size="small"
            aria-label="Zoom in"
            onClick={() => onZoom(0.1)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <ToolButton
        label="Fit page"
        icon={<FitScreenIcon fontSize="small" />}
        active={view.fit === "page"}
        onClick={() => onFit("page")}
      />
      <ToolButton
        label="Fit width"
        icon={<WidthFullIcon fontSize="small" />}
        active={view.fit === "width"}
        onClick={() => onFit("width")}
      />
      <Separator />

      <ToolButton
        label="Pen"
        hint="Pen (P)"
        icon={<CreateOutlinedIcon fontSize="small" />}
        active={tool === "ink"}
        disabled={readOnly}
        onClick={() => onTool("ink")}
      />
      <ToolButton
        label="Highlight"
        hint="Highlighter (I)"
        icon={<BorderColorOutlinedIcon fontSize="small" />}
        active={tool === "highlight"}
        disabled={readOnly}
        onClick={() => onTool("highlight")}
      />
      <ToolButton
        label="Eraser"
        hint="Eraser (E)"
        icon={<AutoFixNormalOutlinedIcon fontSize="small" />}
        active={tool === "eraser"}
        disabled={readOnly}
        onClick={() => onTool("eraser")}
      />
      <ToolMenu
        label="Shape"
        items={SHAPES}
        tool={tool}
        fallback={SHAPES[0]}
        disabled={readOnly}
        onTool={onTool}
      />
      <ToolButton
        label="Text"
        hint="Text comment (T)"
        icon={<TitleIcon fontSize="small" />}
        active={tool === "text"}
        disabled={readOnly}
        onClick={() => onTool("text")}
      />
      <ToolMenu
        label="Stamp"
        items={STAMPS}
        tool={tool}
        fallback={STAMPS[0]}
        disabled={readOnly}
        onTool={onTool}
      />
      <Separator />

      <ToolButton
        label="Undo"
        hint="Undo (Ctrl Z)"
        icon={<UndoIcon fontSize="small" />}
        disabled={!canUndo || readOnly}
        onClick={onUndo}
      />
      <ToolButton
        label="Redo"
        hint="Redo (Ctrl Y)"
        icon={<RedoIcon fontSize="small" />}
        disabled={!canRedo || readOnly}
        onClick={onRedo}
      />

      <Box flex={1} />
      <ToolButton
        label="Tools"
        hint="Show or hide the tools panel"
        icon={<ViewWeekOutlinedIcon fontSize="small" />}
        active={showPanel}
        onClick={onTogglePanel}
      />
    </Box>
  );
}
